import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  FileEdit, Save, RotateCcw, Loader2, Plus, Trash2, ChevronUp, ChevronDown,
  ShieldCheck, Eye, Clock, LayoutTemplate,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { api, fileUrl } from '../../lib/api';
import { RichTextEditor } from '../../components/RichTextEditor';
import { ImageField } from './AdminCrud';

/* Admin -> Page Content
   One simple place to edit the wording that used to be fixed in code:
   - Page headers (kicker / title / intro) on the major public pages
   - Homepage quick-link cards
   - Footer quick-link columns + key CTA button labels
   The approved current wording is the default. Nothing changes publicly
   until a section is edited and saved. Restore Defaults brings the
   original wording back. */

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] font-bold text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const move = (arr, from, to) => {
  if (to < 0 || to >= arr.length) return arr;
  const copy = [...arr];
  const [x] = copy.splice(from, 1);
  copy.splice(to, 0, x);
  return copy;
};

const GroupMeta = ({ override }) => (
  <div className="flex items-center gap-2">
    {override ? (
      <Badge className="bg-[#FBF7EE] text-[#8a6d2c] border border-[#e5d5a5] hover:bg-[#FBF7EE] text-[11px] font-bold" data-testid="page-content-status-badge">Customized</Badge>
    ) : (
      <Badge variant="outline" className="text-[11px] font-bold text-slate-500" data-testid="page-content-status-badge">Default wording</Badge>
    )}
    {override?.updated_at && (
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid="page-content-last-edited">
        <Clock className="h-3 w-3" /> last edited {String(override.updated_at).slice(0, 10)}
      </span>
    )}
  </div>
);

/* ---------------- Header/Hero preview (mirrors the public light hero) ---------------- */
const resolveImg = (url) => (!url ? '' : (url.startsWith('http') || url.startsWith('/branding/')) ? url : fileUrl(url));

const HeaderPreview = ({ kicker, title, intro, hero, image_url, overlay, primary_label, primary_style, secondary_label }) => {
  const ov = Math.min(1, Math.max(0.4, (overlay ?? 85) / 100));
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200" data-testid="page-content-preview">
      <div className="relative bg-[#f3f7ff] px-5 py-7 overflow-hidden">
        {hero && image_url && (
          <>
            <img src={resolveImg(image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '75% center' }} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, rgba(255,255,255,${Math.min(1, ov + 0.12)}) 0%, rgba(255,255,255,${ov}) 38%, rgba(255,255,255,${(ov * 0.45).toFixed(2)}) 68%, rgba(255,255,255,0.06) 100%)` }} />
          </>
        )}
        <div className="relative max-w-md">
          {kicker && <p className="text-[10px] font-bold uppercase tracking-widest text-[#24406f] mb-2">{kicker}</p>}
          <p className="hero-h1 text-xl text-[#0f172a] whitespace-pre-line">{title || '(title required)'}</p>
          {intro && <p className="mt-2 text-sm text-slate-700 leading-relaxed">{intro}</p>}
          {hero && (primary_label || secondary_label) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {primary_label && <span className={`px-3 py-1.5 rounded text-xs font-bold text-white ${primary_style === 'red' ? 'bg-[#b91c1c]' : primary_style === 'black' ? 'bg-[#0b0f17]' : 'bg-[#1a2744]'}`}>{primary_label}</span>}
              {secondary_label && <span className="px-3 py-1.5 rounded text-xs font-bold text-[#1a2744] border-2 border-[#1a2744]/60 bg-white/70">{secondary_label}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------- Header sections editor ---------------- */
const HeadersEditor = ({ groupKey, defaults, override, onSaved, onReset }) => {
  const base = override || defaults;
  const [sections, setSections] = useState(() => {
    const out = {};
    Object.keys(defaults.sections).forEach((sk) => {
      const d = defaults.sections[sk];
      const s = (override?.sections || {})[sk] || d;
      out[sk] = { kicker: s.kicker || '', title: s.title || '', intro: s.intro || '' };
      if (d.hero) {
        out[sk] = { ...out[sk], hero: true,
          image_url: s.image_url ?? d.image_url ?? '',
          image_alt: s.image_alt ?? d.image_alt ?? '',
          focal: s.focal ?? d.focal ?? 'right',
          overlay: s.overlay ?? d.overlay ?? 85,
          primary_label: s.primary_label ?? d.primary_label ?? '',
          primary_url: s.primary_url ?? d.primary_url ?? '',
          primary_style: s.primary_style ?? d.primary_style ?? 'navy',
          secondary_label: s.secondary_label ?? d.secondary_label ?? '',
          secondary_url: s.secondary_url ?? d.secondary_url ?? '',
          show: s.show ?? d.show ?? true };
      }
    });
    return out;
  });
  const [saving, setSaving] = useState(false);
  const [previewOn, setPreviewOn] = useState(true);
  const set = (sk, field, v) => setSections((prev) => ({ ...prev, [sk]: { ...prev[sk], [field]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/page-content/${groupKey}`, { sections });
      toast.success('Saved. The public page now shows this wording.');
      onSaved(r.data.group);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {Object.keys(defaults.sections).map((sk) => {
        const d = defaults.sections[sk];
        const s = sections[sk];
        const label = (defaults.section_labels || {})[sk] || 'Section';
        return (
          <Card key={sk} className="border-slate-200" data-testid={`page-content-section-${groupKey}-${sk}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-bold text-[#1a2744] text-sm">{label}</p>
                <div className="flex items-center gap-3">
                  {d.note && <p className="text-[11px] text-slate-500 max-w-md">{d.note}</p>}
                  {d.hero && (
                    <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">Show hero
                      <Switch checked={s.show !== false} onCheckedChange={(v) => set(sk, 'show', v)} className="scale-75" data-testid={`page-content-hero-show-${groupKey}`} />
                    </label>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <Field label="Small top label (kicker, optional)"><Input value={s.kicker} onChange={(e) => set(sk, 'kicker', e.target.value)} className="h-10" data-testid={`page-content-kicker-${groupKey}-${sk}`} /></Field>
                <Field label={d.hero ? 'Hero H1 (press Enter for a hard line break)' : 'Title'}>
                  <Textarea value={s.title} onChange={(e) => set(sk, 'title', e.target.value)} rows={2} className="text-sm font-bold" data-testid={`page-content-title-${groupKey}-${sk}`} />
                </Field>
              </div>
              <Field label="Supporting line (optional, keep it short)"><Textarea value={s.intro} onChange={(e) => set(sk, 'intro', e.target.value)} rows={2} className="text-sm" data-testid={`page-content-intro-${groupKey}-${sk}`} /></Field>

              {d.hero && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 space-y-3" data-testid={`page-content-hero-fields-${groupKey}`}>
                  <p className="text-xs font-black uppercase tracking-wider text-slate-500">Hero image and buttons</p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div>
                      <Field label="Background image">
                        <ImageField value={s.image_url} onChange={(v) => set(sk, 'image_url', v)} category="page-heroes" accept="image/*" />
                      </Field>
                      <div className="mt-2.5">
                        <Field label="Image ALT text (describes the photo for accessibility)">
                          <Input value={s.image_alt} onChange={(e) => set(sk, 'image_alt', e.target.value)} className="h-10" data-testid={`page-content-alt-${groupKey}`} />
                        </Field>
                      </div>
                    </div>
                    <div className="space-y-2.5">
                      <Field label="Photo focal point (which part of the photo stays visible on the right)">
                        <Select value={s.focal} onValueChange={(v) => set(sk, 'focal', v)}>
                          <SelectTrigger className="h-10 bg-white" data-testid={`page-content-focal-${groupKey}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">Left of the photo</SelectItem>
                            <SelectItem value="center">Centre of the photo</SelectItem>
                            <SelectItem value="right">Right of the photo</SelectItem>
                            <SelectItem value="left-top">Left, keep the top visible</SelectItem>
                            <SelectItem value="center-top">Centre, keep the top visible</SelectItem>
                            <SelectItem value="right-top">Right, keep the top visible</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label={`White overlay strength: ${s.overlay}% (higher = easier to read, less photo)`}>
                        <Slider value={[s.overlay]} min={40} max={100} step={1} onValueChange={([v]) => set(sk, 'overlay', v)} className="py-2" data-testid={`page-content-overlay-${groupKey}`} />
                      </Field>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Main button text"><Input value={s.primary_label} onChange={(e) => set(sk, 'primary_label', e.target.value)} className="h-10" data-testid={`page-content-primary-label-${groupKey}`} /></Field>
                        <Field label="Main button link"><Input value={s.primary_url} onChange={(e) => set(sk, 'primary_url', e.target.value)} placeholder="/page or #section" className="h-10" data-testid={`page-content-primary-url-${groupKey}`} /></Field>
                      </div>
                      <Field label="Main button colour (red = take action)">
                        <Select value={s.primary_style} onValueChange={(v) => set(sk, 'primary_style', v)}>
                          <SelectTrigger className="h-10 bg-white" data-testid={`page-content-primary-style-${groupKey}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="navy">Campaign blue (normal action)</SelectItem>
                            <SelectItem value="red">Red (take action)</SelectItem>
                            <SelectItem value="black">Black (editorial)</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Second button text (optional)"><Input value={s.secondary_label} onChange={(e) => set(sk, 'secondary_label', e.target.value)} className="h-10" data-testid={`page-content-secondary-label-${groupKey}`} /></Field>
                        <Field label="Second button link"><Input value={s.secondary_url} onChange={(e) => set(sk, 'secondary_url', e.target.value)} placeholder="/page" className="h-10" data-testid={`page-content-secondary-url-${groupKey}`} /></Field>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {previewOn && <HeaderPreview {...s} hero={!!d.hero} />}
            </CardContent>
          </Card>
        );
      })}
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="page-content-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
        <Button variant="outline" onClick={() => setPreviewOn((v) => !v)} className="h-10 text-xs font-bold border-slate-300" data-testid="page-content-preview-btn"><Eye className="h-3.5 w-3.5 mr-1.5" /> {previewOn ? 'Hide preview' : 'Show preview'}</Button>
        <Button variant="outline" onClick={onReset} className="h-10 text-xs font-bold border-slate-300 text-slate-600" data-testid="page-content-reset-btn"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore defaults</Button>
      </div>
    </div>
  );
};

/* ---------------- Link list editor (footer columns) ---------------- */
const LinkListEditor = ({ title, links, onChange, testPrefix }) => (
  <Card className="border-slate-200">
    <CardContent className="p-4">
      <p className="font-bold text-[#1a2744] text-sm mb-3">{title}</p>
      <div className="space-y-2" data-testid={`${testPrefix}-rows`}>
        {links.map((l, i) => (
          <div key={i} className={`flex flex-wrap items-center gap-2 border rounded-lg p-2 ${l.show === false ? 'border-dashed border-slate-300 bg-slate-50 opacity-70' : 'border-slate-200'}`}>
            <Input value={l.label} onChange={(e) => onChange(links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Label" className="h-9 flex-1 min-w-[130px] text-sm" data-testid={`${testPrefix}-label-${i}`} />
            <Input value={l.url} onChange={(e) => onChange(links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} placeholder="/page" className="h-9 flex-1 min-w-[130px] text-sm" data-testid={`${testPrefix}-url-${i}`} />
            <div className="flex items-center gap-0.5 shrink-0">
              <Switch checked={l.show !== false} onCheckedChange={(v) => onChange(links.map((x, j) => (j === i ? { ...x, show: v } : x)))} className="scale-75" data-testid={`${testPrefix}-show-${i}`} />
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onChange(move(links, i, i - 1))} disabled={i === 0} aria-label="Move up"><ChevronUp className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onChange(move(links, i, i + 1))} aria-label="Move down"><ChevronDown className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" aria-label="Remove link"
                onClick={() => { if (window.confirm(`Remove the "${l.label || 'unnamed'}" link?\n\nTip: turn its visibility OFF instead if you might want it back.`)) onChange(links.filter((_, j) => j !== i)); }}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" className="h-9 w-full text-xs font-bold border-dashed border-slate-300 text-slate-600" onClick={() => onChange([...links, { label: '', url: '', show: true }])} data-testid={`${testPrefix}-add`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add link
        </Button>
      </div>
    </CardContent>
  </Card>
);

/* ---------------- Footer & Buttons editor ---------------- */
const isEmptyHtml = (h) => !String(h || '').replace(/<[^>]+>|\s|&nbsp;/g, '');

/* ---------------- WYSIWYG section editor (Phase 93) ----------------
   Each page section is edited independently. Leaving a section empty keeps
   the approved built-in wording. Saved HTML is sanitized on the server. */
const RichtextEditor = ({ groupKey, defaults, override, onSaved, onReset }) => {
  const [sections, setSections] = useState(() => {
    const out = {};
    Object.keys(defaults.sections).forEach((sk) => {
      out[sk] = { html: (override?.sections || {})[sk]?.html || '' };
    });
    return out;
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/page-content/${groupKey}`, { sections });
      toast.success('Saved. The public page now shows your text.');
      onSaved(r.data.group);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-[#eaf2ff] border border-[#24406f]/20 p-3.5 text-sm text-[#1a2744]">
        <p className="font-bold">How this works</p>
        <p className="mt-1 text-[13px]">Each section below is independent. Write and save to replace that area of the page. Clear a section and save to bring back the built-in approved wording. Formatting is kept simple on purpose so every page stays readable and mobile-friendly.</p>
        {defaults.page_url && <a href={defaults.page_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1.5 text-[13px] font-bold underline underline-offset-2" data-testid={`richtext-view-page-${groupKey}`}>View the public page</a>}
      </div>
      {Object.keys(defaults.sections).map((sk) => (
        <Card key={sk} className="border-slate-200" data-testid={`richtext-section-${groupKey}-${sk}`}>
          <CardContent className="p-4 space-y-2.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-bold text-[#1a2744] text-sm">{defaults.sections[sk].label}</p>
              <Badge variant="outline" className={!isEmptyHtml(sections[sk].html) ? 'text-emerald-700 border-emerald-300' : 'text-slate-500'}>
                {!isEmptyHtml(sections[sk].html) ? 'Custom text active' : 'Built-in wording'}
              </Badge>
            </div>
            <RichTextEditor value={sections[sk].html} onChange={(v) => setSections((prev) => ({ ...prev, [sk]: { html: v } }))} placeholder="Leave empty to keep the built-in wording…" />
          </CardContent>
        </Card>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`richtext-save-${groupKey}`}>
          {saving ? 'Saving…' : 'Save Sections'}
        </Button>
        <Button variant="outline" onClick={onReset} className="h-11 font-bold btn-press" data-testid={`richtext-reset-${groupKey}`}>Restore Defaults</Button>
      </div>
    </div>
  );
};

const FooterEditor = ({ defaults, override, onSaved, onReset }) => {
  const src = override || defaults;
  const [takeAction, setTakeAction] = useState(() => (src.take_action || []).map((l) => ({ ...l })));
  const [stayInformed, setStayInformed] = useState(() => (src.stay_informed || []).map((l) => ({ ...l })));
  const [headerLabel, setHeaderLabel] = useState(src.header_report_label || '');
  const [drawerLabel, setDrawerLabel] = useState(src.drawer_report_label || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/page-content/footer-buttons', {
        take_action: takeAction, stay_informed: stayInformed,
        header_report_label: headerLabel, drawer_report_label: drawerLabel,
      });
      toast.success('Saved. The footer and buttons are updated on the public site.');
      onSaved(r.data.group);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="page-content-footer-editor">
      <Card className="border-slate-200"><CardContent className="p-4">
        <p className="font-bold text-[#1a2744] text-sm mb-3">Key buttons</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <Field label='Top bar button (desktop header)'><Input value={headerLabel} onChange={(e) => setHeaderLabel(e.target.value)} className="h-10" data-testid="page-content-header-report-label" /></Field>
          <Field label="Hamburger menu button (mobile drawer)"><Input value={drawerLabel} onChange={(e) => setDrawerLabel(e.target.value)} className="h-10" data-testid="page-content-drawer-report-label" /></Field>
        </div>
        <div className="mt-3 rounded-lg bg-[#1a2744] p-3 flex flex-wrap gap-2 items-center" data-testid="page-content-buttons-preview">
          <span className="text-[11px] text-slate-400 mr-1">Preview:</span>
          <span className="px-3 py-1.5 rounded text-xs font-bold bg-[#C9A84C] text-[#1a2744]">{headerLabel || 'Report an Issue'}</span>
          <span className="px-3 py-1.5 rounded text-xs font-bold bg-[#C9A84C] text-[#1a2744]">{drawerLabel || 'Report a Ward 8 Issue'}</span>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LinkListEditor title="Footer column: Take Action" links={takeAction} onChange={setTakeAction} testPrefix="footer-take-action" />
        <LinkListEditor title="Footer column: Stay Informed" links={stayInformed} onChange={setStayInformed} testPrefix="footer-stay-informed" />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="page-content-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
        <Button variant="outline" onClick={onReset} className="h-10 text-xs font-bold border-slate-300 text-slate-600" data-testid="page-content-reset-btn"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore defaults</Button>
      </div>
    </div>
  );
};

/* ---------------- Home cards editor ---------------- */
const HomeEditor = ({ defaults, override, onSaved, onReset }) => {
  const src = override || defaults;
  const [buttons, setButtons] = useState(() => (src.election_buttons || []).map((b) => ({ ...b })));
  const [cards, setCards] = useState(() => (src.action_cards || []).map((c) => ({ ...c })));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/page-content/home', { election_buttons: buttons, action_cards: cards });
      toast.success('Saved. The homepage cards and quick links are updated.');
      onSaved(r.data.group);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="page-content-home-editor">
      <Card className="border-slate-200"><CardContent className="p-4">
        <p className="font-bold text-[#1a2744] text-sm mb-1">Election quick-link buttons</p>
        <p className="text-[11px] text-slate-500 mb-3">The four buttons in the homepage "Who's Running in 2026?" block.</p>
        <div className="space-y-2">
          {buttons.map((b, i) => (
            <div key={b.id} className={`flex flex-wrap items-center gap-2 border rounded-lg p-2 ${b.show === false ? 'border-dashed border-slate-300 bg-slate-50 opacity-70' : 'border-slate-200'}`} data-testid={`home-election-btn-row-${b.id}`}>
              <Input value={b.label} onChange={(e) => setButtons(buttons.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Button label" className="h-9 flex-1 min-w-[140px] text-sm" data-testid={`home-election-btn-label-${b.id}`} />
              <Input value={b.url} onChange={(e) => setButtons(buttons.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} placeholder="/page" className="h-9 flex-1 min-w-[140px] text-sm" data-testid={`home-election-btn-url-${b.id}`} />
              <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600 shrink-0">Show <Switch checked={b.show !== false} onCheckedChange={(v) => setButtons(buttons.map((x, j) => (j === i ? { ...x, show: v } : x)))} className="scale-75" data-testid={`home-election-btn-show-${b.id}`} /></label>
            </div>
          ))}
        </div>
      </CardContent></Card>

      <Card className="border-slate-200"><CardContent className="p-4">
        <p className="font-bold text-[#1a2744] text-sm mb-1">Bottom action cards</p>
        <p className="text-[11px] text-slate-500 mb-3">The three cards at the bottom of the homepage (Join the team / Subscribe / Contact).</p>
        <div className="space-y-3">
          {cards.map((c, i) => (
            <div key={c.id} className={`border rounded-lg p-3 space-y-2.5 ${c.show === false ? 'border-dashed border-slate-300 bg-slate-50 opacity-70' : 'border-slate-200'}`} data-testid={`home-card-row-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-black uppercase tracking-wide text-slate-400">{c.id}</p>
                <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">Show <Switch checked={c.show !== false} onCheckedChange={(v) => setCards(cards.map((x, j) => (j === i ? { ...x, show: v } : x)))} className="scale-75" data-testid={`home-card-show-${c.id}`} /></label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <Field label="Card title"><Input value={c.title} onChange={(e) => setCards(cards.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} className="h-9 text-sm" data-testid={`home-card-title-${c.id}`} /></Field>
                {c.id !== 'subscribe' && (
                  <Field label="Button label"><Input value={c.button_label} onChange={(e) => setCards(cards.map((x, j) => (j === i ? { ...x, button_label: e.target.value } : x)))} className="h-9 text-sm" data-testid={`home-card-button-label-${c.id}`} /></Field>
                )}
              </div>
              <Field label="Card text"><Textarea value={c.body} onChange={(e) => setCards(cards.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))} rows={2} className="text-sm" data-testid={`home-card-body-${c.id}`} /></Field>
              {c.id !== 'subscribe' && (
                <Field label="Button link"><Input value={c.button_url} onChange={(e) => setCards(cards.map((x, j) => (j === i ? { ...x, button_url: e.target.value } : x)))} className="h-9 text-sm" data-testid={`home-card-button-url-${c.id}`} /></Field>
              )}
            </div>
          ))}
        </div>
      </CardContent></Card>

      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="page-content-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
        <Button variant="outline" onClick={onReset} className="h-10 text-xs font-bold border-slate-300 text-slate-600" data-testid="page-content-reset-btn"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore defaults</Button>
      </div>
    </div>
  );
};

/* ---------------- Page ---------------- */
export default function AdminPageContent() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState('home');

  const load = useCallback(() => {
    api.get('/admin/page-content').then((r) => setData(r.data))
      .catch(() => toast.error('Could not load page content'));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const { defaults, order, overrides } = data;
  const activeDefaults = defaults[active];
  const activeOverride = overrides[active] || null;

  const onSaved = (group) => setData((d) => ({ ...d, overrides: { ...d.overrides, [active]: group } }));
  const onReset = async () => {
    if (!window.confirm(`Restore the default wording for "${activeDefaults.label}"?\n\nYour custom text for this section will be removed and the approved original wording will show again.`)) return;
    try {
      await api.post(`/admin/page-content/${active}/reset`);
      toast.success('Default wording restored.');
      setData((d) => {
        const next = { ...d.overrides }; delete next[active];
        return { ...d, overrides: next };
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not restore defaults'); }
  };

  // Force editors to remount when the group or its override state changes
  const editorKey = `${active}-${activeOverride?.updated_at || 'default'}`;

  return (
    <div className="space-y-4" data-testid="admin-page-content">
      <div>
        <h1 className="font-serif text-2xl font-black text-[#1a2744] flex items-center gap-2"><FileEdit className="h-6 w-6" /> Page Content</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Edit the wording on the public pages: headers, homepage cards, footer links, and key buttons.
          The approved current wording is the default. Nothing changes until you save, and Restore Defaults always brings the original wording back.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="page-content-group-nav">
        {order.map((key) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            className={`px-3 py-2 min-h-[40px] rounded-lg text-xs font-bold transition-colors duration-150 ${active === key ? 'bg-[#1a2744] text-white' : 'bg-white border border-slate-200 text-[#1a2744] hover:bg-slate-50'}`}
            data-testid={`page-content-group-${key}`}
          >
            {defaults[key].label}
            {overrides[key] && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#C9A84C] align-middle" aria-label="Customized" />}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-bold text-[#1a2744] flex items-center gap-1.5"><LayoutTemplate className="h-4 w-4" /> {activeDefaults.label}</p>
        <GroupMeta override={activeOverride} />
      </div>

      {activeDefaults.type === 'headers' && (
        <HeadersEditor key={editorKey} groupKey={active} defaults={activeDefaults} override={activeOverride} onSaved={onSaved} onReset={onReset} />
      )}
      {activeDefaults.type === 'richtext' && (
        <RichtextEditor key={editorKey} groupKey={active} defaults={activeDefaults} override={activeOverride} onSaved={onSaved} onReset={onReset} />
      )}
      {activeDefaults.type === 'home' && (
        <HomeEditor key={editorKey} defaults={activeDefaults} override={activeOverride} onSaved={onSaved} onReset={onReset} />
      )}
      {activeDefaults.type === 'footer' && (
        <FooterEditor key={editorKey} defaults={activeDefaults} override={activeOverride} onSaved={onSaved} onReset={onReset} />
      )}

      <Card className="border-slate-200 bg-slate-50"><CardContent className="p-4 text-xs text-slate-600 space-y-1">
        <p className="font-bold text-[#1a2744] flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Safety rules</p>
        <p>• The approved current wording is the default. Nothing changes publicly until you press Save.</p>
        <p>• Retired wording (Action Hub, tax percentage targets, old slogans) is blocked automatically when you save.</p>
        <p>• Restore Defaults removes your custom text for that section and brings back the original wording.</p>
      </CardContent></Card>
    </div>
  );
}
